import {EventAdapter, EventAdapterResult} from './event-adapter-interface';
import {StreamEvent} from '../types';

/**
 * GraphEventAdapter is the default {@link EventAdapter} used by the streaming
 * layer. It understands the event formats currently emitted by LangGraph:
 *
 * 1. Custom events emitted via `on_custom_event` with the actual payload
 *    directly in the `data` field:
 *
 *    {
 *      "event": "on_custom_event",
 *      "run_id": "1f00bb85-a6c7-4803-a9d3-b5c76182ba1f",
 *      "name": "data",
 *      "tags": ["graph:step:1"],
 *      "metadata": {...},
 *      "data": "actual payload content"
 *    }
 *
 * The adapter normalizes both shapes into the common return contract defined in
 * {@link EventAdapter.parse}. Unsupported shapes are surfaced as an `error` so
 * upstream consumers can decide how to handle them.
 */

export class GraphEventAdapter implements EventAdapter {
  readonly name = 'graphEvent';

  parse(raw: string): EventAdapterResult {
    try {
      const parsed = JSON.parse(raw);

      // Handle `on_custom_event` shape from LangGraph - this is the only valid StreamEvent format
      if (
        typeof parsed.event === 'string' &&
        parsed.event === 'on_custom_event' &&
        typeof parsed.run_id === 'string' &&
        parsed.metadata &&
        typeof parsed.metadata === 'object'
      ) {
        const name =
          typeof parsed.name === 'string' && parsed.name
            ? parsed.name
            : parsed.event;

        const streamEvent: StreamEvent = {
          event: parsed.event,
          name,
          data: parsed.data,
          run_id: parsed.run_id,
          tags: parsed.tags,
          metadata: parsed.metadata,
        };

        return streamEvent;
      }

      // Handle status events like {"event": "start"} or {"event": "end"}
      // These are not StreamEvents but we need to handle them gracefully
      if (typeof parsed.event === 'string' && !parsed.run_id) {
        const streamEvent: StreamEvent = {
          event: 'on_custom_event',
          name: parsed.event,
          data: parsed.data || null,
          run_id: 'status-event',
          tags: [],
          metadata: {
            thread_id: 'status',
            checkpoint_ns: 'status',
            langgraph_step: 0,
            langgraph_node: 'status',
            langgraph_path: [],
            __pregel_task_id: 'status',
            __pregel_resuming: false,
            langgraph_triggers: [],
            langgraph_checkpoint_ns: 'status',
          },
        };

        return streamEvent;
      }

      // All other formats are not valid and should return an error
      return {error: new Error('Unsupported event format')};
    } catch (err) {
      return {error: err as Error};
    }
  }
}
